import { Icon, IconButton } from '@smartgift/frontend-toolkit';
import { isArray } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import isEmailValid from 'validator/lib/isEmail';

import { Table } from '@hero/molecules';
import { isNameValid } from '@hero/utils';

const Wrapper = styled.div``;

const SpreadSheetContactTable = ({ contacts, updateContacts, isLoading }) => {
  const { t } = useTranslation();
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [globalFilters, setGlobalFilters] = useState({});

  const pageCount = useMemo(() => {
    return Math.ceil(contacts.length / pageSize);
  }, [pageSize]);

  const contactsByPage = useMemo(() => {
    let pageIndex = 0;
    const result = {};

    while (pageIndex < pageCount) {
      result[pageIndex] = contacts.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize);
      pageIndex += 1;
    }

    return result;
  }, [contacts, pageSize]);

  const columns = React.useMemo(
    () => [
      {
        id: 'email',
        header: t('email'),
        editable: true,
        size: 120,
        accessorKey: 'email',
        isValidFn: val => val && isEmailValid(val),
        componentProps: {
          maxLength: 100
        }
      },
      {
        header: t('firstName'),
        size: 100,
        accessorKey: 'firstName',
        editable: true,
        isValidFn: val => !!val,
        componentProps: {
          maxLength: 30
        }
      },
      {
        header: t('lastName'),
        size: 100,
        accessorKey: 'lastName',
        editable: true,
        isValidFn: val => !!val,
        componentProps: {
          maxLength: 30
        }
      },
      {
        id: 'birthday',
        header: t('birthday'),
        editable: true,
        type: 'date',
        accessorKey: 'birthday',
        componentProps: {
          excludeFuture: true,
          maxDate: new Date()
        }
      },
      {
        id: 'anniversary',
        header: t('workAnniversary'),
        editable: true,
        type: 'date',
        accessorKey: 'anniversary',
        componentProps: {
          excludeFuture: false
        }
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: ({ table, row }) => {
          const { updateContacts } = table.getState();

          return (
            <IconButton
              variant="text"
              color="error"
              onClick={() => {
                updateContacts(contacts => {
                  return contacts.filter(contact => contact.id !== row.id);
                });
              }}
            >
              <Icon name="delete" size="md" />
            </IconButton>
          );
        }
      }
    ],
    []
  );

  const updateData = (originalRow, columnId, value) => {
    const currentValue = originalRow[columnId];

    const isDifferent = isArray(value)
      ? !isEqual(
          currentValue?.map?.(col => col.id),
          value?.map?.(val => val.value)
        )
      : !isEqual(currentValue, value);

    if (!isDifferent) return;

    updateContacts(contacts =>
      contacts?.map(contact => {
        if (contact.id !== originalRow.id) return contact;

        const updatedContact = {
          ...contact,
          [columnId]: value
        };

        updatedContact.hasError = !(
          isEmailValid(updatedContact.email) &&
          isNameValid(updatedContact.firstName) &&
          isNameValid(updatedContact.lastName)
        );

        return updatedContact;
      })
    );
  };

  return (
    <Wrapper>
      <Table
        columns={columns}
        data={contactsByPage[pageIndex] || []}
        state={{
          updateContacts,
          globalFilter: globalFilters
        }}
        updateData={updateData}
        loading={isLoading}
        setPagination={setPagination}
        globalFilters={globalFilters}
        setGlobalFilters={setGlobalFilters}
        useGlobalFuzzyFilter
        manualPagination
        pagination={{
          pageIndex,
          pageSize,
          total: contacts?.length
        }}
        pageCount={pageCount}
        enabled={{ filter: false, header: true }}
      />
    </Wrapper>
  );
};

export { SpreadSheetContactTable };
